import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import api, { formatApiError } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { Button, Card, Spinner } from "../components/ui/Primitives";

export default function BillingSuccessPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("polling"); // polling | paid | failed | error
  const [purpose, setPurpose] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      setError("No session_id");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const MAX = 15; // ~30s
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
        if (cancelled) return;
        if (data.status === "paid") {
          setStatus("paid");
          setPurpose(data.purpose);
          await refresh();
          return;
        }
        if (data.status === "failed" || data.status === "expired") {
          setStatus("failed");
          setPurpose(data.purpose);
          return;
        }
        if (attempts >= MAX) {
          setStatus("error");
          setError(t("billing.success.timeout"));
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setError(formatApiError(e));
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [params, refresh, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-void px-5">
      <Card className="p-8 max-w-md w-full text-center" data-testid="billing-success-page">
        {status === "polling" && (
          <>
            <Loader2 size={40} className="text-brand animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-ink-primary mb-2">{t("billing.success.checking")}</h1>
            <p className="text-sm text-ink-secondary">{t("billing.success.checking_sub")}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={48} className="text-ok mx-auto mb-4" data-testid="billing-success-paid" />
            <h1 className="text-2xl font-semibold text-ink-primary mb-2">{t("billing.success.paid_title")}</h1>
            <p className="text-sm text-ink-secondary mb-6">
              {purpose === "topup"
                ? t("billing.success.paid_topup")
                : t("billing.success.paid_sub")}
            </p>
            <div className="flex gap-2 justify-center">
              <Button variant="primary" onClick={() => navigate("/dashboard")} data-testid="success-go-dashboard">
                {t("billing.success.go_dashboard")}
              </Button>
              <Button variant="secondary" onClick={() => navigate("/billing")}>
                {t("billing.success.go_billing")}
              </Button>
            </div>
          </>
        )}
        {(status === "failed" || status === "error") && (
          <>
            <AlertTriangle size={40} className="text-err mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-ink-primary mb-2">{t("billing.success.error_title")}</h1>
            <p className="text-sm text-ink-secondary mb-5">{error || t("billing.success.error_sub")}</p>
            <Button variant="secondary" onClick={() => navigate("/billing")}>
              {t("billing.success.back")}
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
